import React from 'react'
import { useResizeDetector } from 'react-resize-detector'
import { useMotionValue } from 'framer-motion'

import { AnimatedKnob, Container, Track } from './styles'

const KNOB_SIZE = 16

export interface SliderInputProps {
  /** The minimum value of the slider */
  minValue: number
  /** The maximum value of the slider */
  maxValue: number
  /** Current selected Min value from parent, allows for another input to affect the slider.*/
  selectedMinValue: number
  /** Current selected Max value from parent, allows for another input to affect the slider*/
  selectedMaxValue: number
  /** Allows for a single or dual handle implementation */
  isSingleHandle?: boolean
  /** Callback to set the value of the minimum handle to the parent, used to set text or similar to the value */
  setSliderMin: React.Dispatch<React.SetStateAction<number>>
  /** Callback to set the value of the maximum handle to the parent, used to set text or similar to the value */
  setSliderMax: React.Dispatch<React.SetStateAction<number>>
  /** Callback to indicate when the drag stops. Use this to if you don't want to worry about debouncing/giving feedback on the position
   *  args not needed for this as you can use the states from above.
   */
  onDragEndCallback?: () => void
}

export const SliderInput = (props: SliderInputProps) => {
  const [trackWidth, setTrackWidth] = React.useState(1)
  const minKnob = useMotionValue(1)
  const maxKnob = useMotionValue(1)

  const convertValuesToPercentage = () => {
    const minPercentage = (props.selectedMinValue / props.maxValue) * 100
    const maxPercentage = (props.selectedMaxValue / props.maxValue) * 100
    return { minPercentage, maxPercentage }
  }

  const convertPercentageToKnobPosition = () => {
    const { minPercentage, maxPercentage } = convertValuesToPercentage()
    const minKnobPixels = trackWidth * (minPercentage / 100)
    const maxKnobPixels = trackWidth * (maxPercentage / 100)
    return { minKnobPixels, maxKnobPixels }
  }

  const numberToMotionValue = (value: number) =>
    Number(value / (props.maxValue / trackWidth))

  const motionValueToNumber = (value: number) =>
    Number((props.maxValue / (trackWidth / value)).toFixed(2))

  React.useEffect(() => {
    const { minKnobPixels, maxKnobPixels } = convertPercentageToKnobPosition()
    minKnob.set(minKnobPixels)
    maxKnob.set(maxKnobPixels)
  }, [minKnob.get(), maxKnob.get()])

  React.useEffect(() => {
    // core effect to handle the drag of the handles and firing the callback.
    const minChange = minKnob.onChange((value) => {
      let newValue = motionValueToNumber(value)
      if (newValue < props.minValue) {
        newValue = props.minValue
      }

      props.setSliderMin(newValue)
    })
    const maxChange = maxKnob.onChange((value) => {
      let newValue = motionValueToNumber(value)
      if (props.isSingleHandle && newValue < props.minValue) {
        newValue = props.minValue
      }
      if (newValue > props.maxValue) {
        newValue = props.maxValue
      }

      props.setSliderMax(newValue)
    })

    return () => {
      minChange() // Unsubscribes both min and max change listeners.
      maxChange() // Needed to prevent the slider from firing a lot of changes even after the drag event stops.
    }
  })

  const onResize = React.useCallback((width) => {
    setTrackWidth(width)
  }, [])

  const { ref } = useResizeDetector({
    onResize,
    handleWidth: true,
    refreshMode: 'debounce',
    refreshRate: 100,
  })

  return (
    <Container data-testid="slider-container" ref={ref}>
      <Track {...convertValuesToPercentage()}>
        {!props.isSingleHandle && (
          <AnimatedKnob
            data-testid="slider-input-min-knob"
            drag="x"
            initial={{
              x: minKnob.get(),
            }}
            onDragEnd={() => {
              props.onDragEndCallback?.()
            }}
            dragMomentum={false}
            dragElastic={false}
            style={{ x: minKnob }}
            dragConstraints={{
              left: 0,
              right: numberToMotionValue(props.selectedMaxValue) - KNOB_SIZE,
            }}
          />
        )}
        <AnimatedKnob
          data-testid="slider-input-max-knob"
          drag="x"
          initial={{
            x: maxKnob.get(),
          }}
          onDragEnd={() => props.onDragEndCallback?.()}
          dragMomentum={false}
          dragElastic={false}
          style={{ x: maxKnob }}
          dragConstraints={{
            top: 0,
            bottom: 0,
            left: props.isSingleHandle ? 0 : minKnob.get() + KNOB_SIZE,
            right: trackWidth,
          }}
        />
      </Track>
    </Container>
  )
}
